import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import { NavigationItemExpandButton } from 'components/navigation_drawer/NavigationItemExpandButton';
import { NavigationItemIcon } from 'components/navigation_drawer/NavigationItemIcon';
import { SecondaryNavigationItems } from 'components/navigation_drawer/SecondaryNavigationItems';
import { SecondaryNavigationPopup } from 'components/navigation_drawer/SecondaryNavigationPopup';
import {
    NAV_ACTIVE_STYLE,
    NavigationItem,
    NavigationItemAccordion,
    NavigationItemChevron,
    NavigationItemHeader,
    NavigationItemLeftGroup,
    NavigationItemTitle,
} from 'components/navigation_drawer/styled';
import { usePopup } from 'custom_hooks/usePopup';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { ReactNode } from 'react';
import * as React from 'react';
import { NavLinkProps } from 'react-router-dom';
import { stopEvent } from 'utils/event';

export type NavigationDrawerItemProps = Pick<NavLinkProps, 'exact' | 'isActive'> &
    Readonly<{
        /**
         * A unique ID for the navigation item
         */
        id: string;
        /**
         * The title for the link of this task, should describe the page that the
         * user will be routed to.
         */
        title: string;
        /**
         * The URL to route to when this task is activated.
         */
        route: string;
        /**
         * The Font Awesome icon for this task.
         */
        icon?: IconProp;
        /**
         * A non-font awesome icon for this task.
         */
        customIcon?: ReactNode;
        /**
         * Whether the navigation accordion is expanded or not (collapsed)
         */
        isExpanded?: boolean;
        /**
         * Whether the navigation drawer is expanded or not (collapsed)
         */
        isDrawerExpanded?: boolean;
        /**
         * Whether to display the navigation drawer item or not
         */
        enabled: boolean;
        /**
         * Secondary navigation items
         */
        secondaryNavigationItems?: NavigationDrawerItemProps[];
        /**
         * Callback for when the item is clicked
         */
        onClick?: () => void;
    }>;

export function NavigationDrawerItem({
    id,
    title,
    route,
    icon,
    customIcon,
    isActive,
    isExpanded,
    isDrawerExpanded,
    exact = true,
    secondaryNavigationItems,
    onClick,
}: NavigationDrawerItemProps) {
    if (!customIcon && !icon) {
        throw new Error('Define either icon or customIcon');
    }

    const { isOpen, open, close, triggerRef, popupRef } = usePopup<HTMLDivElement>();

    const { setDrawerExpanded } = useNavigationDrawerValues((state) => ({
        setDrawerExpanded: state.setDrawerExpanded,
    }));

    const isSmallScreen = useIsViewportAtMost(MediaSize.small);

    const conditionalProps = isDrawerExpanded
        ? {
              $isExpanded: isExpanded,
              $isDrawerExpanded: isDrawerExpanded,
          }
        : {
              $isExpanded: false,
              $isDrawerExpanded: false,
              onMouseEnter: open,
              onMouseLeave: close,
          };

    const onItemClick = (
        event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
    ) => {
        if (secondaryNavigationItems && isDrawerExpanded) {
            stopEvent(event);
            onClick?.();
        } else if (isSmallScreen) {
            onClick?.();
            setDrawerExpanded(false);
        }
    };

    return (
        <NavigationItemAccordion>
            <NavigationItem
                key={id}
                exact={exact}
                to={route}
                title={title}
                isActive={isActive}
                activeStyle={NAV_ACTIVE_STYLE}
                style={isOpen ? NAV_ACTIVE_STYLE : undefined}
                {...conditionalProps}
            >
                <NavigationItemHeader onClick={onItemClick}>
                    <NavigationItemLeftGroup>
                        <NavigationItemIcon
                            id={`navigation-drawer-item-${id}`}
                            icon={icon}
                            customIcon={customIcon}
                            ref={triggerRef}
                        />
                        <NavigationItemTitle>{title}</NavigationItemTitle>
                    </NavigationItemLeftGroup>
                    {secondaryNavigationItems && (
                        <NavigationItemChevron>
                            <NavigationItemExpandButton
                                expandType="downward"
                                expanded={isExpanded ?? false}
                                onClick={onItemClick}
                            />
                        </NavigationItemChevron>
                    )}
                </NavigationItemHeader>
                {isOpen && (
                    <SecondaryNavigationPopup
                        parentId={id}
                        title={title}
                        popupRef={popupRef}
                        triggerRef={triggerRef}
                        secondaryNavigationItems={secondaryNavigationItems}
                        onClose={close}
                    />
                )}
            </NavigationItem>
            <SecondaryNavigationItems
                parentId={id}
                show={isExpanded ?? false}
                items={secondaryNavigationItems}
            />
        </NavigationItemAccordion>
    );
}
